import { Injectable } from '@angular/core';

import { CommonWindow } from '@core/domain/models/common-window.model';
import { DeviceService } from '@shared/services/devices.service';

@Injectable({ providedIn: 'root' })
export class AkamaiService {
    public static useAkamaiProductImage(
        window: CommonWindow,
        url: string,
        width: number,
        impolicy: string,
        isColorCut = false
    ): string {
        if (/&impolicy/.exec(url)) {
            return;
        }
        if (!isColorCut) {
            const replacer = (match, p1) => {
                // p1 is nondigits, p2 digits, and p3 non-alphanumerics
                return `_16.${p1}`;
            };
            // eslint-disable-next-line no-param-reassign
            url = url.replace(/_\d{1,2}\.(jpg|png|gif)/, replacer);
        }
        return AkamaiService.useAkamaiImageProducts(window, url, width, impolicy);
    }

    // Receives an url with jpg
    // @param width  the real size of the scaled picture
    public static useAkamaiImage(window: CommonWindow, url: string, width: number, impolicy: string): string {
        let _url = new URL(url);

        if (window && AkamaiService.shouldUseAkamai(width, window)) {
            return AkamaiService.cleanUrlFromParameters(_url);
        }

        return AkamaiService.generateAkamaiUrl(_url, width, impolicy, window);
    }

    // #region PRIVATE METHODS
    private static useAkamaiImageProducts(
        window: CommonWindow,
        url: string,
        width: number,
        impolicy: string
    ): string {
        if (url.includes('idMedia')) {
            return AkamaiService.generateAmandaUrl(url, width, window);
        }

        let _url = new URL(url);

        if (AkamaiService.shouldUseAkamai(width, window)) {
            return AkamaiService.cleanUrlFromParameters(_url);
        }

        return AkamaiService.generateAkamaiUrl(_url, width, impolicy, window);
    }

    private static getWidthAkamai(width: number, window: CommonWindow): number {
        const multiplier = (window?.devicePixelRatio || 1) * 1.2;
        let _width = width * multiplier;
        _width = Math.ceil(_width / 100);
        return _width * 100;
    }

    private static shouldUseAkamai(width: number, window: CommonWindow): boolean {
        const percent = 70; // Si ocupan más del porcentaje definido se excluyen de alamai
        const widthByPercentMin = Math.floor(window.innerWidth * percent) / 100;

        // Para pantallas superiores a 2500 y con densidades superiores a 1x si ocupan + del 90% de la pantalla -> servimos imagen sin akamai
        const maxWidthLargeScreen: number = 2500;
        return window.innerWidth > maxWidthLargeScreen && window.devicePixelRatio > 1 && width >= widthByPercentMin;
    }

    private static cleanUrlFromParameters(url: URL): string {
        const searchParams = new URLSearchParams(url.search);
        searchParams.forEach((value, key) => {
            if (key !== 't') {
                searchParams.delete(key);
            }
        });
        // eslint-disable-next-line no-param-reassign
        url.search = searchParams.toString();
        return url.toString();
    }

    private static generateAkamaiUrl(url: URL, width: number, impolicy: string, window?: CommonWindow): string {
        const _width = width ? AkamaiService.getWidthAkamai(width, window) : null;
        const quality = impolicy || 'massimodutti-itxmediumhigh';

        url.searchParams.set('impolicy', quality);
        if (window && _width) {
            // Client
            const platformAvailable = AkamaiService.platformAvailableIm(window);
            url.searchParams.set('imformat', platformAvailable);
            url.searchParams.set('imwidth', _width.toString());
        } else {
            // Server En Servidor no tenemos tamaño de ventana (si el width se extrae del tamaño de la caja esta no tiene medida)
            url.searchParams.set('imformat', 'generic');
            url.searchParams.set('imwidth', '1');
        }

        return url.toString();
    }

    private static generateAmandaUrl(url: string, width: number, window: CommonWindow): string {
        let _width = AkamaiService.getWidthAkamai(width, window);
        // ECOMDUTI-20195 Gestión de imagenes sin akamai Amanda
        if (!_width) {
            _width = 0;
        }
        return url.replace(':width:', _width.toString());
    }

    private static platformAvailableIm(window: CommonWindow): string {
        const browserName = DeviceService.getBrowserWithoutInjectPlatform(window);
        let browser = ['chrome', 'safari', 'edge'].includes(browserName) ? browserName : 'generic';
        // si es edge como resultado debe ser chrome
        if (browser === 'edge') {
            browser = 'chrome';
        }
        return browser;
    }
    // #endregion PRIVATE METHODS
}
